import type {
  AgentBuildProgressEvent,
  AgentConfirmEvent,
  AgentEntityRowsEvent,
  AgentRunInput,
  AgentStepEvent,
  AgentStreamEvent,
  AgentTranscriptTurn,
  CommandResult,
} from '@travel-planner/shared/schemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { trpcVanilla } from '@/shared/api/trpc';
import { toastStore } from '@/shared/ui';
import { useAgentTranscriptStore } from './use-agent-transcript-store';

export type UseAgentRunOptions = {
  // Invoked for each streamed step that carried a tool `CommandResult`. The
  // panel uses this to apply page-action results (apply_filters) to the page
  // via the adapter — the loop's equivalent of the scripted flow's
  // `handlePageActionResult`.
  onStepResult?: (result: CommandResult) => void;
};

// Live driver for the Agent Loop Engine (US-5 #301 + US-6 #302). Calls the
// streaming `agentRunStream` procedure and surfaces each `step` event the
// moment it arrives, then the `final` text. When the chain made reversible
// writes, the final event carries an `undoToken`; the hook surfaces the global
// Undo toast wired to `agentUndo`, which reverses the whole chain in order.

export type AgentRunStatus = 'idle' | 'running' | 'done' | 'error';

// One entry in the live run timeline (P2.7): a finished tool step, an
// `entity_rows` card group a tool emitted mid-run, or a `confirm` card for a
// paused `requiresConfirm` tool (P5.1). Kept in arrival order so the progress
// view renders them inline with the steps. `build_progress` is NOT in here —
// it's a latest-wins live indicator, surfaced separately.
export type AgentTimelineItem =
  | AgentStepEvent
  | AgentEntityRowsEvent
  | AgentConfirmEvent;

// Shown on the spinner row whenever the loop is running but no tool is mid-
// flight — i.e. while the model is planning its next step.
const THINKING_LABEL = 'Thinking…';

export type UseAgentRun = {
  run: (input: AgentRunInput) => Promise<void>;
  // Resume the last budget-stopped run (P4.1): re-invokes with the server's
  // `continuationToken` so the loop picks up where it stopped. The prior
  // steps stay on the timeline; new steps append. No-op when the last final
  // didn't carry a token.
  keepGoing: () => Promise<void>;
  // True when the last run stopped on the step budget with work still to do —
  // the panel offers the "Keep going" control.
  canContinue: boolean;
  // Resolve a paused `requiresConfirm` tool (P5.1) — approve runs it, reject
  // skips it. Targets the current run's gate by `callId`. No-op if there's no
  // in-flight run id (the stream already settled).
  confirm: (callId: string, decision: 'approve' | 'reject') => Promise<void>;
  // The call ids the consultant has already decided on this run, so the matching
  // confirm card can lock its buttons. Driven by `confirm`.
  resolvedConfirmIds: ReadonlySet<string>;
  reset: () => void;
  undo: () => Promise<void>;
  // The goal the consultant submitted for the current/last run — echoed in
  // the panel so they see their own message.
  goal: string | null;
  steps: AgentStepEvent[];
  // Steps + `entity_rows` card groups in arrival order, for inline rendering
  // (P2.7). `steps` above is the step-only projection kept for back-compat.
  timeline: AgentTimelineItem[];
  // Latest coarse build-progress beat ("Day 3 of 7"), or null when the run
  // isn't a multi-step build. Latest-wins; cleared on a new run and on `final`.
  buildProgress: AgentBuildProgressEvent | null;
  // The label of the tool currently executing (from a 'running' step event),
  // or a neutral "Thinking…" while the model decides its next move. Drives the
  // live spinner row so the consultant always sees what's happening now.
  activeLabel: string | null;
  finalText: string | null;
  budgetExhausted: boolean;
  undoToken: string | null;
  status: AgentRunStatus;
};

export const useAgentRun = (options: UseAgentRunOptions = {}): UseAgentRun => {
  const { onStepResult } = options;
  // Keep the latest callback in a ref so `run` (a stable callback) always
  // calls the current handler without re-subscribing.
  const onStepResultRef = useRef(onStepResult);
  onStepResultRef.current = onStepResult;

  const [goal, setGoal] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<AgentTimelineItem[]>([]);
  const [buildProgress, setBuildProgress] =
    useState<AgentBuildProgressEvent | null>(null);
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const [finalText, setFinalText] = useState<string | null>(null);
  const [budgetExhausted, setBudgetExhausted] = useState(false);
  const [undoToken, setUndoToken] = useState<string | null>(null);
  const [canContinue, setCanContinue] = useState(false);
  const [status, setStatus] = useState<AgentRunStatus>('idle');
  // Call ids the consultant has decided on this run (P5.1) — locks the matching
  // confirm card's buttons once the decision is sent.
  const [resolvedConfirmIds, setResolvedConfirmIds] = useState<Set<string>>(
    () => new Set()
  );

  // "Keep going" state (P4.1): the last run's input + the server's resume
  // token. Refs (not state) — only the `keepGoing` callback reads them.
  const lastInputRef = useRef<AgentRunInput | null>(null);
  const continuationTokenRef = useRef<string | null>(null);
  // The current run's server id (P5.1) — set as soon as a confirm beat (or the
  // final) arrives, so the exported `confirm` callback can target its gate.
  const runIdRef = useRef<string | null>(null);

  // Abort the in-flight stream on unmount so we don't set state after the
  // component is gone (and so the server stops the loop).
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const undo = useCallback(
    async (token?: string) => {
      const target = token ?? undoToken;
      if (!target) return;
      setUndoToken(null);
      await trpcVanilla.agentPanel.agentUndo.mutate({ token: target });
    },
    [undoToken]
  );

  // Resolve a paused `requiresConfirm` tool (P5.1). Marks the card resolved
  // first (so its buttons lock immediately), then sends the decision; the
  // server resolves the gate, which unblocks (approve) or skips (reject) the
  // suspended tool and the stream resumes on the same connection.
  const confirm = useCallback(
    async (callId: string, decision: 'approve' | 'reject') => {
      const runId = runIdRef.current;
      if (!runId) return;
      setResolvedConfirmIds((prev) => new Set(prev).add(callId));
      await trpcVanilla.agentPanel.agentConfirm.mutate({
        runId,
        callId,
        decision,
      });
    },
    []
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    lastInputRef.current = null;
    continuationTokenRef.current = null;
    runIdRef.current = null;
    setGoal(null);
    setTimeline([]);
    setBuildProgress(null);
    setActiveLabel(null);
    setFinalText(null);
    setBudgetExhausted(false);
    setUndoToken(null);
    setCanContinue(false);
    setResolvedConfirmIds(new Set());
    setStatus('idle');
  }, []);

  // Shared driver for a fresh run and a "Keep going" resume (P4.1). A resume
  // keeps the existing timeline (new steps append, so the consultant sees one
  // continuous chain) and sends the server's continuation token alongside the
  // original input.
  const start = useCallback(
    async (input: AgentRunInput, continuationToken?: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const resuming = continuationToken !== undefined;
      setGoal(input.goal);
      if (!resuming) {
        setTimeline([]);
        setUndoToken(null);
      }
      // Each run mints a new id + gate namespace (P5.1) — clear the prior run's
      // id and decisions so a confirm can't target a stale gate and resumed
      // cards start unlocked.
      runIdRef.current = null;
      setResolvedConfirmIds(new Set());
      setBuildProgress(null);
      setActiveLabel(THINKING_LABEL);
      setFinalText(null);
      setBudgetExhausted(false);
      setCanContinue(false);
      continuationTokenRef.current = null;
      setStatus('running');

      // Conversation memory (P2.5): replay the recent turns + the stable session
      // id so the server renders a "Recent conversation" prompt block. The store
      // owns the ring buffer; we record this turn once it finishes (below).
      const transcriptStore = useAgentTranscriptStore.getState();
      // Scope the buffer to this page context (resets on a context switch), then
      // replay the recent turns + the stable session id.
      const sessionId = transcriptStore.prepareForContext(input.contextType);
      const runInput = {
        ...input,
        sessionId,
        transcript: useAgentTranscriptStore.getState().turns,
        // Resume token (P4.1) — only on a "Keep going" re-invoke.
        ...(resuming ? { continuationToken } : {}),
      } as AgentRunInput;

      // Collected locally so we can record the finished turn — React state is
      // async and not readable synchronously after the loop.
      const finishedSteps: AgentStepEvent[] = [];
      let finalTextValue = '';
      let runId: string | undefined;

      try {
        const stream = await trpcVanilla.agentPanel.agentRunStream.mutate(
          runInput,
          {
            context: { stream: true },
            signal: controller.signal,
          }
        );

        let token: string | null = null;
        for await (const event of stream as AsyncIterable<AgentStreamEvent>) {
          if (controller.signal.aborted) return;
          if (event.kind === 'step') {
            if (event.status === 'running') {
              // In-flight beat: drive the spinner label, don't append it — the
              // matching success/error event lands when the tool finishes.
              setActiveLabel(event.label);
              continue;
            }
            finishedSteps.push(event);
            setTimeline((prev) => [...prev, event]);
            // Tool finished; the model plans its next move until the next
            // 'running' beat (or the final event) arrives.
            setActiveLabel(THINKING_LABEL);
            // Apply page-action results (apply_filters) live, as the step
            // arrives — same effect the scripted command flow has on the page.
            if (event.result) onStepResultRef.current?.(event.result);
          } else if (event.kind === 'entity_rows') {
            // A tool streamed a group of entities (P2.7) — append it to the
            // timeline so the cards render inline, in arrival order with steps.
            setTimeline((prev) => [...prev, event]);
          } else if (event.kind === 'build_progress') {
            // Coarse build beat (P2.7) — latest-wins live indicator, not an
            // append-only card. Cleared when the run settles (final, below).
            setBuildProgress(event);
          } else if (event.kind === 'confirm') {
            // A `requiresConfirm` tool paused the loop (P5.1). Capture the run
            // id so `confirm` can target its gate, and append a confirm card —
            // the consultant's Approve/Reject resolves it via `agentConfirm`.
            runId = event.runId;
            runIdRef.current = event.runId;
            setActiveLabel(THINKING_LABEL);
            setTimeline((prev) => [...prev, event]);
          } else if (event.kind === 'final') {
            setActiveLabel(null);
            setBuildProgress(null);
            finalTextValue = event.finalText;
            runId = event.runId;
            runIdRef.current = event.runId ?? runIdRef.current;
            setFinalText(event.finalText);
            setBudgetExhausted(event.budgetExhausted);
            // Budget-stopped with work to do (P4.1): stash the resume token and
            // surface the "Keep going" control.
            setCanContinue(event.canContinue === true);
            continuationTokenRef.current = event.continuationToken ?? null;
            token = event.undoToken;
            setUndoToken(event.undoToken);
          }
        }
        if (controller.signal.aborted) return;
        setStatus('done');

        // Record the finished turn into the conversation buffer so the next call
        // can resolve back-references. Actions are the succeeded steps' labels +
        // result summaries (compact, never full payloads).
        const actions = finishedSteps
          .filter((s) => s.status === 'success')
          .slice(0, 20)
          .map((s) => ({
            label: s.label,
            summary: s.result?.humanSummary ?? s.detail ?? '',
          }));
        const turn: AgentTranscriptTurn = {
          goal: input.goal,
          finalText: finalTextValue,
          ...(actions.length ? { actions } : {}),
          undoToken: token,
          ...(runId ? { runId } : {}),
          at: new Date().toISOString(),
        };
        useAgentTranscriptStore.getState().recordTurn(turn);

        // Reversible writes → surface the one-click Undo toast (US-6). Firing
        // the undo clears the token so a second click is a no-op.
        if (token) {
          const t = token;
          toastStore.getState().show({
            message: 'Done — changes applied.',
            action: {
              label: 'Undo',
              onClick: () => {
                setUndoToken(null);
                // Keep the cross-turn buffer in sync: this run's chain is the
                // session's latest, so clear it so the persistent "Undo last
                // change" control doesn't offer an already-consumed token.
                useAgentTranscriptStore.getState().markLatestUndone();
                void trpcVanilla.agentPanel.agentUndo.mutate({ token: t });
              },
            },
          });
        }
      } catch (_err) {
        // A failed or aborted run records no transcript turn — only completed
        // turns (reached above, after `setStatus('done')`) feed conversation
        // memory, so a half-run can't be replayed as context.
        if (!controller.signal.aborted) setStatus('error');
      }
    },
    []
  );

  const run = useCallback(
    async (input: AgentRunInput) => {
      lastInputRef.current = input;
      await start(input);
    },
    [start]
  );

  // Resume the last budget-stopped run (P4.1). One-shot: the token is cleared
  // as the resume starts (and the server consumes it regardless).
  const keepGoing = useCallback(async () => {
    const input = lastInputRef.current;
    const token = continuationTokenRef.current;
    if (!input || !token) return;
    await start(input, token);
  }, [start]);

  // Step-only projection of the timeline — back-compat for callers that only
  // render steps (and the existing tests). The cards live in `timeline`.
  const steps = timeline.filter(
    (item): item is AgentStepEvent => item.kind === 'step'
  );

  return {
    run,
    keepGoing,
    canContinue,
    confirm,
    resolvedConfirmIds,
    reset,
    undo,
    goal,
    steps,
    timeline,
    buildProgress,
    activeLabel,
    finalText,
    budgetExhausted,
    undoToken,
    status,
  };
};
